/**
 * LazyTab — reusable wrapper for tab content in MainApp.
 *
 * Previously every tab was triple-wrapped:
 *   <ErrorBoundary fallbackMessage="...">
 *     <Suspense fallback={<TabLoadingFallback />}>
 *       <TabWrapper tabKey="...">
 *         <SomeTab ... />
 *       </TabWrapper>
 *     </Suspense>
 *   </ErrorBoundary>
 *
 * Now:
 *   <LazyTab tabKey="..." fallbackMessage="...">
 *     <SomeTab ... />
 *   </LazyTab>
 */
import React, { Suspense } from "react";
import ErrorBoundary from "../ErrorBoundary";
import { SkeletonText, SkeletonTitle, SkeletonListItem, SkeletonSummaryCard } from "../ui/SkeletonLoaders";

// Skeleton ghost mimicking a typical tab layout (header + cards + list)
const TabLoadingFallback = () => (
  <div className="fade-in" style={{ padding: "20px 4px" }}>
    <div style={{ marginBottom: "24px" }}>
      <SkeletonTitle width="30%" />
      <SkeletonText width="55%" height="13px" />
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "14px",
        marginBottom: "24px",
      }}
    >
      <SkeletonSummaryCard />
      <SkeletonSummaryCard />
      <SkeletonSummaryCard />
    </div>
    {[0, 1, 2, 3].map((i) => (
      <SkeletonListItem key={i} />
    ))}
  </div>
);

// Wrapper that fades-in content on tabKey change
const TabFade = ({ children, tabKey }) => (
  <div
    key={tabKey}
    className="fade-in"
    style={{ animation: "fadeIn 0.3s ease-out", width: "100%" }}
  >
    {children}
  </div>
);

export default function LazyTab({
  tabKey,
  fallbackMessage = "Wystąpił błąd w module",
  children,
}) {
  return (
    <ErrorBoundary fallbackMessage={fallbackMessage}>
      <Suspense fallback={<TabLoadingFallback />}>
        <TabFade tabKey={tabKey}>{children}</TabFade>
      </Suspense>
    </ErrorBoundary>
  );
}

export { TabLoadingFallback };
